import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Reveal } from '@/components/layout/Reveal';
import { useI18n } from '@/lib/i18n';
import { useCinematicStage } from '@/hooks/useCinematicStage';

/**
 * The quieter companion to the services reel.
 *
 * A champagne thread draws itself across the four numbered badges while the
 * text beneath each badge fans out at slightly staggered rates. The badges
 * themselves stay perfectly seated on the thread — the depth comes from the
 * copy moving, not the anchors. Enough to feel filmic, never enough to
 * compete with the section above it (this block also appears on the Home page).
 */
const Step = ({ item, index, progress, animate }) => {
  // Each column's copy drifts a little faster than the one before it, so the
  // row gently fans as you scroll instead of moving as one slab.
  const drift = 5 + index * 2.5;
  const copyY = useTransform(progress, [0.1, 0.85], [drift, -drift]);

  return (
    <Reveal delay={index * 0.08}>
      <div
        data-testid={`home-process-step-${item.step}`}
        className="group h-full p-7 transition-colors duration-500 sm:p-8"
        style={{ backgroundColor: 'var(--rmh-canvas)' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--rmh-surface)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--rmh-canvas)';
        }}
      >
        {/* stays on the thread */}
        <span
          className="inline-flex h-12 w-12 items-center justify-center rounded-full border transition-colors duration-500 group-hover:border-[color:var(--rmh-accent)]"
          style={{
            borderColor: 'var(--rmh-border-strong)',
            backgroundColor: 'var(--rmh-surface-2)',
          }}
        >
          <span className="rmh-num text-[0.8rem] text-[color:var(--rmh-mocha)]">
            {item.step}
          </span>
        </span>

        <motion.div
          style={animate ? { y: copyY } : undefined}
          className={animate ? 'rmh-willchange' : undefined}
        >
          <h3 className="mt-6 font-serif text-[1.18rem] text-[color:var(--rmh-ink)] transition-transform duration-500 group-hover:translate-x-[2px]">
            {item.title}
          </h3>
          <p className="rmh-body mt-3 text-[0.875rem]">{item.body}</p>
        </motion.div>
      </div>
    </Reveal>
  );
};

export const ProcessSteps = () => {
  const { t } = useI18n();
  const { enabled } = useCinematicStage({ minWidth: 1024, minHeight: 620 });
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // The connecting thread draws in as the row enters, then holds.
  const threadScaleX = useTransform(scrollYProgress, [0.18, 0.62], [0, 1]);

  return (
    <div
      ref={sectionRef}
      className="relative mt-14"
      // `relative` is required: the champagne thread is absolutely positioned
      // against this wrapper.
      style={{ position: 'relative' }}
    >
      {/* champagne thread through the badge centres (desktop only) */}
      {enabled ? (
        <motion.span
          aria-hidden="true"
          style={{ scaleX: threadScaleX, transformOrigin: 'left' }}
          className="rmh-willchange pointer-events-none absolute left-0 top-[3.25rem] hidden h-px w-full sm:top-[3.5rem] lg:block"
        >
          <span
            className="block h-px w-full"
            style={{
              backgroundImage:
                'linear-gradient(90deg, rgba(201,176,138,0) 0%, rgba(201,176,138,0.85) 12%, rgba(201,176,138,0.85) 88%, rgba(201,176,138,0) 100%)',
            }}
          />
        </motion.span>
      ) : null}

      <div
        data-testid="home-process-steps"
        className="relative grid grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-4"
        style={{ backgroundColor: 'var(--rmh-border)' }}
      >
        {t.process.map((item, i) => (
          <Step
            key={item.step}
            item={item}
            index={i}
            progress={scrollYProgress}
            animate={enabled}
          />
        ))}
      </div>
    </div>
  );
};

export default ProcessSteps;
